import { Component, EventEmitter, OnDestroy, OnInit, Output } from "@angular/core"
import { FormControl, FormGroup, Validators } from "@angular/forms"
import { SchoolDistrictDropdownService } from "../data-privacy-select/data-privacy-school-form/school-districts-dropdown.service"
import { SchoolDropdownService } from "../data-privacy-select/data-privacy-school-form/schools-dropdown.service"
import { BehaviorSubject, combineLatest, Subject } from "rxjs"
import { UserDataPrivacyService } from "../../../services/user-data-privacy.service"
import {
    ISchool,
    ISchoolDistrict,
    IState,
    PLACEHOLDER_LABEL,
} from "../../../models/userDataPrivacy.model"
import { catchError, filter, switchMap, takeUntil, tap } from "rxjs/operators"
import Utils from "../../../utils/utils"
import { NotificationsService } from "angular2-notifications"
import { StatesDropdownService } from "../data-privacy-select/data-privacy-school-form/states-dropdown.service"

@Component({
    selector: "app-data-privacy-support",
    templateUrl: "./data-privacy-support.component.html",
    styleUrls: ["./data-privacy-support.component.scss"],
    providers: [SchoolDistrictDropdownService, SchoolDropdownService, StatesDropdownService],
    standalone: false,
})
export class DataPrivacySupportComponent implements OnInit, OnDestroy {
    destroyed$ = new Subject()

    isFeedbackFormCollapsed = true

    schoolUpdateForm: FormGroup = new FormGroup({
        schoolState: new FormControl(null, Validators.required),
        schoolDistrict: new FormControl(null, Validators.required),
        schoolName: new FormControl(null),
    })
    schoolUpdateFormInvalid = true

    placeholderText$ = new BehaviorSubject<PLACEHOLDER_LABEL>(PLACEHOLDER_LABEL.HAS_SCHOOLS)

    schoolUpdateFormStatusChanges$ = this.schoolUpdateForm?.statusChanges.pipe(
        takeUntil(this.destroyed$),
        tap((formStatus) => (this.schoolUpdateFormInvalid = formStatus === "INVALID"))
    )
    schoolStateFormControlValueChanges$ = this.schoolStateFormControl.valueChanges.pipe(
        takeUntil(this.destroyed$),
        filter((value: IState): boolean => !!value),
        tap((value: IState): void => {
            this.schoolDistrictFormControl.reset()
            this.schoolNameFormControl.reset()
            this.schoolDistrictFormControl.enable()
            this.schoolNameFormControl.disable()
            this.schoolDistrictService.selectedItem$.next(null)
            this.schoolDistrictService.fetchData$.next(value.state_location)
        })
    )

    schoolDistrictFormControlValueChanges$ = this.schoolDistrictFormControl.valueChanges.pipe(
        takeUntil(this.destroyed$),
        filter((value: ISchoolDistrict): boolean => !!value),
        tap((value: ISchoolDistrict): void => {
            this.schoolNameFormControl.reset()
            this.schoolNameFormControl.enable()
            this.schoolService.selectedItem$.next(null)
            this.schoolService.fetchData$.next(value.district_guid)
        })
    )

    schoolServiceCount$ = this.schoolService.count$.pipe(
        takeUntil(this.destroyed$),
        tap((count: number): void => {
            if (count > 0) {
                this.placeholderText$.next(PLACEHOLDER_LABEL.HAS_SCHOOLS)
                this.schoolNameFormControl.enable()
            }
        })
    )

    @Output() changeSchoolState: EventEmitter<IState> = new EventEmitter<IState>()
    @Output() changeSchool: EventEmitter<ISchool> = new EventEmitter<ISchool>()
    @Output() changeSchoolDistrict: EventEmitter<ISchoolDistrict> =
        new EventEmitter<ISchoolDistrict>()

    constructor(
        public schoolDistrictService: SchoolDistrictDropdownService,
        public schoolService: SchoolDropdownService,
        private userDataPrivacyService: UserDataPrivacyService,
        public notificationsService: NotificationsService,
        public statesService: StatesDropdownService
    ) {}

    get schoolStateFormControl(): FormControl {
        return this.schoolUpdateForm.get("schoolState") as FormControl
    }

    get schoolDistrictFormControl(): FormControl {
        return this.schoolUpdateForm.get("schoolDistrict") as FormControl
    }

    get schoolNameFormControl(): FormControl {
        return this.schoolUpdateForm.get("schoolName") as FormControl
    }

    ngOnInit() {
        this.schoolDistrictFormControl.disable()
        this.schoolNameFormControl.disable()
        this.schoolUpdateFormStatusChanges$.subscribe()
        this.schoolStateFormControlValueChanges$.subscribe()
        this.schoolDistrictFormControlValueChanges$.subscribe()
        this.schoolServiceCount$.subscribe()
    }

    onSubmit(): void {
        const schoolDistrict = this.schoolUpdateForm.value.schoolDistrict
        const schoolNameID = this.schoolUpdateForm.value.schoolName
            ? this.schoolUpdateForm.value.schoolName.id
            : null
        let payload: any = {
            district_guid: schoolDistrict.district_guid,
        }
        if (schoolNameID) payload.school_id = schoolNameID
        this.userDataPrivacyService
            .updateDataPrivacy(payload)
            .pipe(
                takeUntil(this.destroyed$),
                catchError((response: any) => {
                    throw response
                })
            )
            .subscribe(() => {
                this.changeSchoolDistrict.emit(
                    this.schoolDistrictFormControl.value as ISchoolDistrict
                )
                this.changeSchoolState.emit(this.schoolStateFormControl.value as IState)
                if (!!schoolNameID) {
                    this.changeSchool.emit(this.schoolNameFormControl.value)
                } else {
                    this.changeSchool.emit({ name: "Not supplied" })
                }
                this.notificationsService.success(
                    "Success",
                    "School information updated!",
                    Utils.alertConfiguration
                )
                this.isFeedbackFormCollapsed = true
            })
    }

    ngOnDestroy(): void {
        this.destroyed$.next()
        this.destroyed$.complete()
    }
}
